import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { axiosInstance } from '../../config/axios';
import { useAuth } from '../../hooks/useAuth';
import { useUnitats } from '../../hooks/useUnitats';
import useFetch from '../../hooks/useFetch';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
	PageTitle,
	WideContentContainer,
} from '../Shared/CommonStyles'; // Import shared styles

import * as S from './Calendar.styles';

// Define the type locally
interface EventDocument {
	_id: string;
	title: string;
	description: string;
	startDate: Date | string;
	endDate: Date | string;
	unitat: string;
}

interface SchoolYearSelectorProps {
	onSchoolYearChange: (value: string) => void;
	schoolYear: string;
}

const UNITATS = ['agrupament', 'cills', 'llids', 'rings', 'pics', 'trucs'] as const;
type UnitatType = typeof UNITATS[number];

interface UnitatToggleButtonsProps {
	activeUnitats: Set<UnitatType>;
	onToggle: (unitat: UnitatType) => void;
	onToggleAll: () => void;
	isAllActive: boolean;
}

const UnitatToggleButtons: FC<UnitatToggleButtonsProps> = ({
	activeUnitats,
	onToggle,
	onToggleAll,
	isAllActive,
}) => {
	const { unitats } = useUnitats();

	const getUnitatAbrev = (unitatKey: string) => {
		if (unitatKey.toLowerCase() === 'agrupament') return 'AGR';
		const unitat = unitats?.find((u) => u.abreviacio.toLowerCase() === unitatKey);
		return unitat?.abreviacio || unitatKey.toUpperCase().slice(0, 4);
	};

	return (
		<S.UnitatToggleContainer>
			<S.FilterLabel>Filtrar per:</S.FilterLabel>
			{UNITATS.map((unitat) => (
				<S.UnitatToggleButton
					key={unitat}
					unitat={unitat}
					isActive={activeUnitats.has(unitat)}
					onClick={() => onToggle(unitat)}
					type="button"
				>
					{getUnitatAbrev(unitat)}
				</S.UnitatToggleButton>
			))}
			<S.AllToggleButton
				isAllActive={isAllActive}
				onClick={onToggleAll}
				type="button"
			>
				{isAllActive ? 'Cap' : 'Tots'}
			</S.AllToggleButton>
		</S.UnitatToggleContainer>
	);
};

const SchoolYearSelector: FC<SchoolYearSelectorProps> = ({
	onSchoolYearChange,
	schoolYear,
}) => {
	const handleSchoolYearChangeButtonClick = (year: 'next' | 'prev') => {
		const [firstYear, secondYear] = schoolYear.split('-');

		if (year === 'next') {
			onSchoolYearChange(
				`${parseInt(firstYear) + 1}-${parseInt(secondYear) + 1}`
			);
		} else {
			onSchoolYearChange(
				`${parseInt(firstYear) - 1}-${parseInt(secondYear) - 1}`
			);
		}
	};

	return (
		<S.SchoolYearSelectorContainer>
			<button onClick={() => handleSchoolYearChangeButtonClick('prev')}>
				Curs anterior
			</button>
			<p>Curs {schoolYear}</p>
			<button onClick={() => handleSchoolYearChangeButtonClick('next')}>
				Curs següent
			</button>
		</S.SchoolYearSelectorContainer>
	);
};

const months = [
	'Setembre', // 8
	'Octubre', // 9
	'Novembre', // 10
	'Desembre', // 11
	'Gener', // 0
	'Febrer', // 1
	'Març', // 2
	'Abril', // 3
	'Maig', // 4
	'Juny', // 5
	'Juliol', // 6
	'Agost', // 7
];

const jsMonthToSchoolYearMonth: { [key: number]: string } = {
	0: 'Gener', // January
	1: 'Febrer', // February
	2: 'Març', // March
	3: 'Abril', // April
	4: 'Maig', // May
	5: 'Juny', // June
	6: 'Juliol', // July
	7: 'Agost', // August
	8: 'Setembre', // September
	9: 'Octubre', // October
	10: 'Novembre', // November
	11: 'Desembre', // December
};

const getMonthName = (date: Date) => {
	return jsMonthToSchoolYearMonth[date.getMonth()];
};

const getCurrentSchoolYear = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth(); // 0-11

	// If month is September (8) or later, we are in the start of a school year (Year - Year+1)
	// If month is before September, we are in the end of a school year (Year-1 - Year)
	if (month >= 8) {
		return `${year}-${year + 1}`;
	} else {
		return `${year - 1}-${year}`;
	}
};

const Calendar = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const [selectedEvent, setSelectedEvent] = useState<EventDocument | null>(null);

	// Initialize activeUnitats from URL params or default to all
	const [activeUnitats, setActiveUnitats] = useState<Set<UnitatType>>(() => {
		const preParam = searchParams.get('pre');
		if (preParam) {
			const preselected = preParam.split(',').filter((u): u is UnitatType =>
				UNITATS.includes(u as UnitatType)
			);
			return new Set(preselected);
		}
		return new Set(UNITATS);
	});

	const isAllActive = activeUnitats.size === UNITATS.length;

	const [schoolYear, setSchoolYear] = useState<string>(
		getCurrentSchoolYear()
	);

	const { user } = useAuth();
	const { unitats } = useUnitats();

	const {
		data: events,
		isLoading: isFetchingEvents,
		error: fetchEventsError,
	} = useFetch<EventDocument[]>({
		queryKey: ['events', schoolYear],
		url: `/api/calendari/events?unitat=agrupament&schoolYear=${schoolYear}`,
	});

	// Client-side filtering by active unitats
	const filteredEvents = useMemo(() => {
		if (!events) return [];
		return events.filter((event) => activeUnitats.has(event.unitat as UnitatType));
	}, [events, activeUnitats]);

	const deleteMutation = useMutation({
		mutationFn: async (id: string) => {
			await axiosInstance.delete(`/api/calendari/events/${id}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['events', schoolYear] });
		},
	});

	const canManageEvent = (eventUnitat: string) => {
		if (!user) return false;
		if (
			user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') // trusted curators: any event
		) {
			return true;
		}
		if (user.roles.includes('Unitat') && user.unitat === eventUnitat) {
			return true;
		}
		return false;
	};

	const canCreateEvent = () => {
		if (!user) return false;
		return (
			user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') ||
			user.roles.includes('Unitat')
		);
	};

	const handleEventCreateClick = () => {
		navigate('/calendari/crear');
	};

	const handleEditClick = (id: string) => {
		navigate(`/calendari/editar/${id}`);
	};

	const handleDeleteClick = (id: string) => {
		if (window.confirm('Estàs segur que vols eliminar aquest event?')) {
			deleteMutation.mutate(id);
		}
	};

	const handleUnitatToggle = (unitat: UnitatType) => {
		setActiveUnitats((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(unitat)) {
				newSet.delete(unitat);
			} else {
				newSet.add(unitat);
			}
			return newSet;
		});
	};

	const handleToggleAll = () => {
		if (isAllActive) {
			setActiveUnitats(new Set());
		} else {
			setActiveUnitats(new Set(UNITATS));
		}
	};

	const getUnitatAbrev = (unitatKey: string) => {
		if (unitatKey.toLowerCase() === 'agrupament') return 'AGR';
		const unitat = unitats?.find((u) => u.abreviacio.toLowerCase() === unitatKey);
		return unitat?.abreviacio || unitatKey.toUpperCase();
	};

	const handleCardClick = (event: EventDocument) => {
		setSelectedEvent(event);
	};

	const handleCloseModal = () => {
		setSelectedEvent(null);
	};

	// Accessibility of the event modal: real dialog semantics, Escape to
	// close, focus moved into the dialog on open, and Tab kept inside.
	const modalContentRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		if (!selectedEvent) return;
		const root = modalContentRef.current;
		root?.querySelector<HTMLButtonElement>('button')?.focus();
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				handleCloseModal();
				return;
			}
			if (e.key !== 'Tab' || !root) return;
			const focusables = [
				...root.querySelectorAll<HTMLElement>(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
				),
			].filter((el) => !el.hasAttribute('disabled'));
			if (focusables.length === 0) {
				e.preventDefault();
				return;
			}
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [selectedEvent]);

	const handleModalEditClick = () => {
		if (selectedEvent) {
			navigate(`/calendari/editar/${selectedEvent._id}`);
		}
	};

	const handleModalDeleteClick = () => {
		if (selectedEvent && window.confirm('Estàs segur que vols eliminar aquest event?')) {
			deleteMutation.mutate(selectedEvent._id);
			setSelectedEvent(null);
		}
	};

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			setSelectedEvent(null);
		}
	};

	const renderEventCard = (event: EventDocument) => {
		const dateInfo = formatEventDate(event);
		const unitatAbrev = getUnitatAbrev(event.unitat);

		return (
			<S.EventCard
				key={event._id}
				unitat={event.unitat}
				onClick={() => handleCardClick(event)}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handleCardClick(event);
					}
				}}
			>
				<S.EventDate unitat={event.unitat}>
					{dateInfo.isSingleDay ? (
						<>
							<S.Day>{dateInfo.startDay}</S.Day>
							<S.MonthSmall>{dateInfo.month}</S.MonthSmall>
						</>
					) : dateInfo.sameMonth ? (
						<>
							<S.DayRange>{dateInfo.startDay} - {dateInfo.endDay}</S.DayRange>
							<S.MonthSmall>{dateInfo.month}</S.MonthSmall>
						</>
					) : (
						<>
							<S.Day>{dateInfo.startDay}</S.Day>
							<S.MonthSmall>{dateInfo.startMonth}</S.MonthSmall>
							<S.Day>{dateInfo.endDay}</S.Day>
							<S.MonthSmall>{dateInfo.endMonth}</S.MonthSmall>
						</>
					)}
				</S.EventDate>
				<S.EventContent>
					<S.TitleRow>
						<S.EventTitle>{event.title}</S.EventTitle>
						<S.UnitatMiniLabel unitat={event.unitat}>{unitatAbrev}</S.UnitatMiniLabel>
					</S.TitleRow>
					<S.EventDescription>{event.description}</S.EventDescription>
				</S.EventContent>
				{canManageEvent(event.unitat) && (
					<S.ActionButtons>
						<S.EditButton
							onClick={(e) => {
								e.stopPropagation();
								handleEditClick(event._id);
							}}
						>
							Editar
						</S.EditButton>
						<S.DeleteButton
							onClick={(e) => {
								e.stopPropagation();
								handleDeleteClick(event._id);
							}}
						>
							Eliminar
						</S.DeleteButton>
					</S.ActionButtons>
				)}
			</S.EventCard>
		);
	};

	// TODO Arreglar events final d'any (campaments d'agost a setembre)
	const eventsByMonth = useMemo(() => {
		if (!filteredEvents) return {};

		const grouped: { [key: string]: EventDocument[] } = {};
		months.forEach((month) => {
			grouped[month] = [];
		});

		filteredEvents.forEach((event) => {
			const date = new Date(event.startDate);
			const monthName = getMonthName(date);
			if (grouped[monthName]) {
				grouped[monthName].push(event);
			}
		});

		// Sort events by date within each month
		Object.keys(grouped).forEach((month) => {
			grouped[month].sort((a, b) => {
				const dateA = new Date(a.startDate);
				const dateB = new Date(b.startDate);
				return dateA.getTime() - dateB.getTime();
			});
		});

		return grouped;
	}, [filteredEvents]);

	const formatEventDate = (event: EventDocument) => {
		const startDate = new Date(event.startDate);
		const endDate = event.endDate ? new Date(event.endDate) : null;

		const formatDay = (date: Date) => {
			return date.getDate();
		};

		const formatMonth = (date: Date) => {
			const month = date.toLocaleDateString('ca-ES', {
				month: 'long',
			});
			// Add "d'" before vowels, "de " before consonants
			const firstLetter = month.toLowerCase().charAt(0);
			const isVowel = ['a', 'e', 'i', 'o', 'u'].includes(firstLetter);
			return isVowel ? `d'${month}` : `de ${month}`;
		};

		if (!endDate || startDate.getTime() === endDate.getTime()) {
			return {
				startDay: formatDay(startDate),
				endDay: null,
				month: formatMonth(startDate),
				isSingleDay: true,
			};
		}

		// Same month: abbreviated form
		if (startDate.getMonth() === endDate.getMonth()) {
			return {
				startDay: formatDay(startDate),
				endDay: formatDay(endDate),
				month: formatMonth(endDate),
				isSingleDay: false,
				sameMonth: true,
			};
		}

		// Different months: full form
		return {
			startDay: formatDay(startDate),
			endDay: formatDay(endDate),
			startMonth: formatMonth(startDate),
			endMonth: formatMonth(endDate),
			isSingleDay: false,
			sameMonth: false,
		};
	};

	const loading = isFetchingEvents || deleteMutation.isPending;
	const error = fetchEventsError || deleteMutation.error;

	return loading ? (
		<WideContentContainer>
			<LoadingMessage>
				<LoadingGif size="large" />
			</LoadingMessage>
		</WideContentContainer>
	) : error ? (
		<WideContentContainer>
			<ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>
		</WideContentContainer>
	) : events ? (
		<WideContentContainer>
			<PageTitle>Calendari</PageTitle>
			<S.ControlsContainer>
				<UnitatToggleButtons
					activeUnitats={activeUnitats}
					onToggle={handleUnitatToggle}
					onToggleAll={handleToggleAll}
					isAllActive={isAllActive}
				/>
				<SchoolYearSelector
					onSchoolYearChange={setSchoolYear}
					schoolYear={schoolYear}
				/>
			</S.ControlsContainer>

			{Object.values(eventsByMonth).some(
				(events) => events.length > 0
			) ? (
					months
						.filter((month) => eventsByMonth[month]?.length > 0)
						.map((month) => (
							<S.MonthContainer key={month}>
								<S.MonthTitle>{month}</S.MonthTitle>
								<S.EventsContainer>
									{eventsByMonth[month]?.map((event) =>
										renderEventCard(event)
									)}
								</S.EventsContainer>
							</S.MonthContainer>
						))
				) : (
					<S.MonthContainer>
						<S.EventsContainer>
							<p>No hi ha events programats per aquest curs.</p>
						</S.EventsContainer>
					</S.MonthContainer>
				)}

			{canCreateEvent() && (
				<S.CreateEventButton onClick={handleEventCreateClick}>
					Crear event
				</S.CreateEventButton>
			)}

			{selectedEvent && (
				<S.ModalOverlay onClick={handleOverlayClick}>
					<S.ModalContent
						ref={modalContentRef}
						role="dialog"
						aria-modal="true"
						aria-label={selectedEvent.title}
					>
						<S.ModalHeader>
							<S.ModalTitle>{selectedEvent.title}</S.ModalTitle>
							<S.ModalClose onClick={handleCloseModal}>
								&times;
							</S.ModalClose>
						</S.ModalHeader>
						<S.ModalBody>
							<S.ModalUnitatLabel unitat={selectedEvent.unitat}>
								{getUnitatAbrev(selectedEvent.unitat)}
							</S.ModalUnitatLabel>
							<S.ModalDate>
								<strong>Data:</strong>{' '}
								{(() => {
									const dateInfo = formatEventDate(selectedEvent);
									if (dateInfo.isSingleDay) {
										return `${dateInfo.startDay} ${dateInfo.month}`;
									} else if (dateInfo.sameMonth) {
										return `${dateInfo.startDay} - ${dateInfo.endDay} ${dateInfo.month}`;
									} else {
										return `${dateInfo.startDay} ${dateInfo.startMonth} - ${dateInfo.endDay} ${dateInfo.endMonth}`;
									}
								})()}
							</S.ModalDate>
							<S.ModalDescription>
								{selectedEvent.description || 'Sense descripció'}
							</S.ModalDescription>
						</S.ModalBody>
						{canManageEvent(selectedEvent.unitat) && (
							<S.ModalActions>
								<S.ModalEditButton onClick={handleModalEditClick}>
									Editar
								</S.ModalEditButton>
								<S.ModalDeleteButton onClick={handleModalDeleteClick}>
									Eliminar
								</S.ModalDeleteButton>
							</S.ModalActions>
						)}
					</S.ModalContent>
				</S.ModalOverlay>
			)}
		</WideContentContainer>
	) : (
		<WideContentContainer>
			<ErrorMessage>
				Error: No s&apos;ha pogut carregar el calendari
			</ErrorMessage>
		</WideContentContainer>
	);
};

export default Calendar;

